<template>
  <div
    :class="['metric-chart', `metric-chart--${kind}`]"
    :style="{ width: totalWidth + 'px' }"
    @mousedown="onPointerDown"
    @mousemove="onPointerMove"
    @mouseleave="clearHover"
    @click="onClick"
  >
    <svg
      class="metric-svg"
      :viewBox="`0 0 ${safeMaxSeconds} 100`"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        v-for="band in bands"
        :key="band"
        :class="['metric-bars', `metric-bars--${band}`]"
        :d="paths[band]"
      />
      <line
        v-if="Number.isFinite(displayMeanScaled)"
        class="metric-baseline"
        x1="0"
        :x2="safeMaxSeconds"
        :y1="(1 - displayMeanScaled) * 100"
        :y2="(1 - displayMeanScaled) * 100"
      />
    </svg>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  points: { type: Array, default: () => [] },
  kind: {
    type: String,
    required: true,
    validator: value => ['loudness', 'speech-rate'].includes(value)
  },
  pxPerSec: { type: Number, default: 1 },
  maxSeconds: { type: Number, default: 1 },
  stepSeconds: { type: Number, default: 1 },
  meanValue: { type: Number, default: Number.NaN }
})

const emit = defineEmits(['seek'])
const bands = ['base', 'top10', 'top5', 'top3', 'top1']
const hovered = ref(null)
const pointerDownX = ref(null)

const clamp = value => Math.min(1, Math.max(0, Number(value) || 0))
const safeMaxSeconds = computed(() => Math.max(1, Number(props.maxSeconds) || 1))
const totalWidth = computed(() => safeMaxSeconds.value * Math.max(0.5, Number(props.pxPerSec) || 1))

const rawMetricValue = point => Number(
  props.kind === 'speech-rate' ? point.speech_rate_cpm : point.rms_dbfs
)

const percentile = (ordered, quantile) => {
  if (!ordered.length) return Number.NaN
  if (ordered.length === 1) return ordered[0]
  const position = (ordered.length - 1) * quantile
  const lowerIndex = Math.floor(position)
  const upperIndex = Math.min(ordered.length - 1, lowerIndex + 1)
  const weight = position - lowerIndex
  return ordered[lowerIndex] * (1 - weight) + ordered[upperIndex] * weight
}

const metricStats = computed(() => {
  const ordered = (props.points || [])
    .map(rawMetricValue)
    .filter(Number.isFinite)
    .sort((a, b) => a - b)

  return {
    // Preserve the complete observed range. Percentiles are used only for
    // highlighting peak regions, never for clipping chart heights.
    lower: ordered[0],
    upper: ordered[ordered.length - 1],
    top10: percentile(ordered, 0.90),
    top5: percentile(ordered, 0.95),
    top3: percentile(ordered, 0.97),
    top1: percentile(ordered, 0.99)
  }
})

const scaleForDisplay = value => {
  const { lower, upper } = metricStats.value
  if (![value, lower, upper].every(Number.isFinite)) return Number.NaN
  if (upper <= lower) return 0.5
  return clamp((value - lower) / (upper - lower))
}

const getPeakBand = value => {
  const stats = metricStats.value
  if (!Number.isFinite(value)) return 'base'
  if (value >= stats.top1) return 'top1'
  if (value >= stats.top3) return 'top3'
  if (value >= stats.top5) return 'top5'
  if (value >= stats.top10) return 'top10'
  return 'base'
}

const displayMeanScaled = computed(() => scaleForDisplay(Number(props.meanValue)))

const normalizedPoints = computed(() => {
  return (props.points || []).map((point, index, allPoints) => {
    if (props.kind === 'speech-rate') {
      const start = Number(point.start_sec || 0)
      const nextStart = Number(allPoints[index + 1]?.start_sec)
      const end = Number.isFinite(nextStart) && nextStart > start
        ? nextStart
        : start + Math.max(0.01, Number(props.stepSeconds) || 1)
      const rawValue = rawMetricValue(point)
      const band = getPeakBand(rawValue)
      const displayValue = scaleForDisplay(rawValue)
      return {
        start,
        end,
        value: Number.isFinite(displayValue) ? displayValue : clamp(point.speech_rate_scaled),
        band
      }
    }

    const start = Number(point.start_sec ?? point.sec ?? 0)
    const end = Number(point.end_sec ?? (start + 1))
    const rawValue = rawMetricValue(point)
    const displayValue = scaleForDisplay(rawValue)
    const scaledValue = Number(point.loudness_scaled)
    const value = Number.isFinite(displayValue)
      ? displayValue
      : Number.isFinite(scaledValue)
        ? clamp(scaledValue)
      : clamp(Math.sqrt(Math.max(0, Number(point.rms || 0))) * 1.5)
    const band = getPeakBand(rawValue)
    return {
      start,
      end,
      value,
      band
    }
  }).filter(point => Number.isFinite(point.start) && Number.isFinite(point.end))
})

const paths = computed(() => {
  const result = Object.fromEntries(bands.map(band => [band, '']))
  for (const point of normalizedPoints.value) {
    const top = (1 - Math.max(0.04, point.value)) * 100
    result[point.band] += `M${point.start},100V${top}H${point.end}V100Z`
  }
  return result
})

const findPoint = sec => {
  const points = normalizedPoints.value
  let low = 0
  let high = points.length - 1
  while (low <= high) {
    const mid = (low + high) >> 1
    const point = points[mid]
    if (sec < point.start) high = mid - 1
    else if (sec >= point.end) low = mid + 1
    else return point
  }
  return null
}

const onPointerDown = event => {
  pointerDownX.value = event.clientX
}

const onPointerMove = event => {
  const rect = event.currentTarget.getBoundingClientRect()
  const localX = event.clientX - rect.left
  hovered.value = findPoint(localX / Math.max(0.5, Number(props.pxPerSec) || 1))
}

const clearHover = () => {
  hovered.value = null
  pointerDownX.value = null
}

const onClick = event => {
  if (pointerDownX.value !== null && Math.abs(event.clientX - pointerDownX.value) <= 3 && hovered.value) {
    emit('seek', hovered.value.start)
  }
  pointerDownX.value = null
}
</script>

<style scoped>
.metric-chart {
  position: relative;
  height: 100%;
  z-index: 6;
}

.metric-svg {
  display: block;
  width: 100%;
  height: 100%;
  shape-rendering: crispEdges;
}

.metric-baseline {
  stroke: rgba(255, 255, 255, 0.45);
  stroke-width: 1;
  stroke-dasharray: 4 3;
  vector-effect: non-scaling-stroke;
}

.metric-bars { opacity: 0.92; }
.metric-bars--base { fill: #3949ab; opacity: 0.34; }
.metric-bars--top10 { fill: #7e57c2; }
.metric-bars--top5 { fill: #ffb74d; }
.metric-bars--top3 { fill: #ff7043; }
.metric-bars--top1 { fill: #ff1744; }

.metric-chart--loudness .metric-bars--base { fill: #00acc1; }
</style>
